interface DownloadableFile {
  url: string;
  originalName: string;
}

interface DownloadableBlob {
  blob: Blob;
  originalName: string;
}

function saveBlob({ blob, originalName }: DownloadableBlob): void {
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = originalName;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking in the same task can cancel the download in some browsers, so let the click settle.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export function downloadBlob(file: DownloadableBlob): void {
  saveBlob(file);
}

/**
 * Save a file to disk instead of navigating to it.
 *
 * A file url is an app route that redirects to object storage on another origin, and browsers drop
 * the `download` attribute across a cross-origin redirect — so a plain `<a download>` navigates the
 * current tab out of the app rather than downloading. Fetching the bytes and handing the browser a
 * same-origin blob url is what makes the download real.
 *
 * Storage CORS is not guaranteed in every environment, so a fetch that is blocked (or fails) falls
 * back to opening the file in a new tab, which is what the rest of the app does.
 */
export async function downloadFile(file: DownloadableFile): Promise<void> {
  try {
    const response = await fetch(file.url);

    if (!response.ok) {
      throw new Error(`Unable to download file (${response.status}).`);
    }

    const blob = await response.blob();
    saveBlob({ blob, originalName: file.originalName });
  } catch {
    window.open(file.url, '_blank', 'noopener');
  }
}
